// ViewDocRecipients — who this went to, where each of them stands, and the one
// action the viewer offers for a document still in flight: send it again.
//
// Spec decision 4: "Resend/remind also available from the viewer." Nothing here
// signs, cancels or voids — those live on the thread page, where the blast
// radius is stated. A resend is the safe, repeatable action, and it is the one
// an operator wants while actually looking at the half-finished document.

import { useState } from 'react';
import { ApiError } from '@/lib/api';
import { toast } from '@/components/Toast';
import { ConsequenceConfirm } from '@/components/oc/ConsequenceConfirm';
import { OcEmpty, StatusChip } from '@/components/oc/bits';
import { docStateTone } from '@/lib/viewDocs';
import { resendDoc, type DocRecipient } from '@/lib/viewDocsClient';

function resendError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    // 409 messaging_disabled is the WHP_OWN_MESSAGING gate, not a fault — the
    // server's own sentence explains it better than a generic failure would.
    return body?.message ?? body?.error ?? `Couldn't send it again (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't send it again.";
}

export function ViewDocRecipients({
  kind,
  id,
  recipients,
  onChanged,
}: {
  kind: string;
  id: string;
  recipients: DocRecipient[];
  onChanged: () => void;
}): React.JSX.Element {
  const [target, setTarget] = useState<DocRecipient | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!target) return;
    setBusy(true);
    try {
      // The route accepts either form — a dashboard row id (`${kind}:${groupId}:…`)
      // or a bare recipient id — and resolves both to the same dispatch, so we
      // send whichever the payload gave us.
      const res = await resendDoc(kind, id, { recipient: target.resendId ?? target.id });
      if (res.failed > 0) {
        const reason = res.results?.find((r) => !r.ok)?.reason;
        throw new Error(reason ? `Couldn't send it again: ${reason}` : "Couldn't send it again.");
      }
      toast.success(`Sent again to ${target.name}. The reminder clock restarted.`);
      setTarget(null);
      onChanged();
    } catch (err) {
      toast.error(resendError(err));
    } finally {
      setBusy(false);
    }
  };

  if (recipients.length === 0) {
    return <OcEmpty>This has not been sent to anyone yet.</OcEmpty>;
  }

  return (
    <>
      <div>
        {recipients.map((r) => (
          <div key={r.id} className="vd-recipient">
            <span className="vd-recipient-facts">
              <span className="vd-recipient-name">{r.name}</span>
              {r.roleLabel && <span className="oc-meta">{r.roleLabel}</span>}
              {r.status && (
                <StatusChip tone={docStateTone(r.status)}>
                  {r.statusLabel?.trim() || r.status}
                </StatusChip>
              )}
            </span>
            {r.canResend && (
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                disabled={busy}
                onClick={() => setTarget(r)}
              >
                Send again
              </button>
            )}
          </div>
        ))}
      </div>

      <ConsequenceConfirm
        open={target !== null}
        title={`Send this to ${target?.name ?? 'them'} again?`}
        consequence={`This emails ${
          target?.name ?? 'them'
        } their existing link one more time and restarts the reminder clock. It is the same document and the same link — nothing is re-issued, and nobody else is emailed.`}
        confirmLabel="Send again"
        tone="default"
        busy={busy}
        onConfirm={() => void run()}
        onCancel={() => {
          if (!busy) setTarget(null);
        }}
      />
    </>
  );
}
